export const SMS_ALERT_FREQUENCY_LOGIN_ONLY = "login_only";
export const SMS_ALERT_FREQUENCY_ONCE_A_DAY = "once_a_day";
export const SMS_ALERT_FREQUENCY_CUSTOM_MINUTES = "custom_minutes";

// Product decision (SOF-329 / GAP-01): "once a day" is a true calendar day.
// The Laravel label hid an apparent 60-minute implementation; the target
// records the intended interval as 1,440 minutes unless Product states
// otherwise. Keep this the single source of truth for the day interval.
export const SMS_ALERT_ONCE_A_DAY_MINUTES = 1440;

export const SMS_ALERT_ONCE_A_DAY_INTERVAL_MS = SMS_ALERT_ONCE_A_DAY_MINUTES * 60 * 1000;

export const SMS_ALERT_FREQUENCY_OPTIONS = [
  {
    value: SMS_ALERT_FREQUENCY_LOGIN_ONLY,
    label: "Only at the time of login",
    description: "Show the low SMS alert once when a user logs in.",
  },
  {
    value: SMS_ALERT_FREQUENCY_ONCE_A_DAY,
    label: "Once in a day",
    description:
      "Show the low SMS alert once per day while monthly SMS remaining is below {{threshold}}.",
  },
  {
    value: SMS_ALERT_FREQUENCY_CUSTOM_MINUTES,
    label: "Set custom time in minutes",
    description:
      "Repeat the alert at your chosen interval while monthly SMS remaining is below {{threshold}}. Default is {{default_minutes}} minutes.",
  },
];

export const SMS_ALERT_THRESHOLD_DESCRIPTION =
  "Default is {{default_threshold}}. The popup appears while monthly SMS remaining is less than this number.";

export const SMS_ALERT_CUSTOM_MINUTES_DESCRIPTION =
  "Default is {{default_minutes}} minutes. The alert will keep repeating until monthly SMS remaining is {{threshold}} or more.";

export const formatSmsAlertCopy = (template, values) =>
  Object.entries(values || {}).reduce(
    (text, [key, value]) => text.split(`{{${key}}}`).join(String(value)),
    template,
  );

export const SMS_ALERT_SETTING_DEFAULTS = {
  threshold: 0,
  frequency: SMS_ALERT_FREQUENCY_LOGIN_ONLY,
  custom_minutes: 30,
};

const FREE_TEXT_MINUTES = { min: 1, max: 10080 };

const toInteger = (value, { min, max, fallback }) => {
  const parsed =
    typeof value === "number"
      ? value
      : typeof value === "string" && value.trim() !== ""
        ? Number(value)
        : NaN;
  if (!Number.isInteger(parsed) || parsed < min || parsed > max) return fallback;
  return parsed;
};

export const getSmsAlertSettings = (settings) => {
  let parsed = settings;
  if (typeof settings === "string") {
    try {
      parsed = JSON.parse(settings);
    } catch (error) {
      parsed = {};
    }
  }
  const merged = {
    ...SMS_ALERT_SETTING_DEFAULTS,
    ...(parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {}),
  };

  const frequency = SMS_ALERT_FREQUENCY_OPTIONS.some(
    (option) => option.value === merged.frequency,
  )
    ? merged.frequency
    : SMS_ALERT_SETTING_DEFAULTS.frequency;

  return {
    threshold: toInteger(merged.threshold, {
      min: 0,
      max: 1000000,
      fallback: SMS_ALERT_SETTING_DEFAULTS.threshold,
    }),
    frequency,
    custom_minutes: toInteger(merged.custom_minutes, {
      min: FREE_TEXT_MINUTES.min,
      max: FREE_TEXT_MINUTES.max,
      fallback: SMS_ALERT_SETTING_DEFAULTS.custom_minutes,
    }),
  };
};

export const validateSmsAlertSettings = (settings) => {
  const errors = {};
  const threshold = settings ? settings.threshold : undefined;
  const isThresholdValid =
    (typeof threshold === "number" && Number.isInteger(threshold)) ||
    (typeof threshold === "string" && threshold.trim() !== "");
  if (!isThresholdValid) {
    errors.threshold = "Enter the remaining-balance threshold.";
  } else if (Number(threshold) < 0 || !Number.isInteger(Number(threshold))) {
    errors.threshold = "The threshold must be a whole number of 0 or more.";
  }

  if (settings && settings.frequency === SMS_ALERT_FREQUENCY_CUSTOM_MINUTES) {
    const minutes = settings.custom_minutes;
    const parsedMinutes = Number(minutes);
    if (
      minutes === "" ||
      minutes === null ||
      minutes === undefined ||
      !Number.isInteger(parsedMinutes) ||
      parsedMinutes < FREE_TEXT_MINUTES.min ||
      parsedMinutes > FREE_TEXT_MINUTES.max
    ) {
      errors.custom_minutes = `Enter a number of minutes between ${FREE_TEXT_MINUTES.min} and ${FREE_TEXT_MINUTES.max}.`;
    }
  }
  return errors;
};
